import { components } from "@certn/api/generated-schemas/client-portal";
import {
  Breadcrumbs,
  AvatarWithName,
  MediaQuery,
  mq,
  Picker,
  PrimaryButton,
  Text,
  NeutralTag,
  Link,
  GhostIconButton,
  DialogTrigger,
  Popover,
  PopoverContent,
  Stack,
  PopoverHeading,
  PopoverActions,
} from "@certn/cascadia-react";
import { useMediaQuery } from "@certn/cascadia-react/hooks/useMediaQuery";
import { breakpoints } from "@certn/cascadia-tokens";
import { parseName } from "@certn/utils";
import { css } from "@emotion/css";
import { Key as AriaKey } from "@react-types/shared";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Key, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useUser } from "../../hooks/useUser";
import { useCanViewCaseScore } from "../permissions/Permissions";
import { CaseDetailActionsDropdown } from "../../pages/case-details/components/CaseDetailActionsDropdown";
import { CaseMonitoringButton } from "../../pages/case-details/components/CaseMonitoringButton";
import { renderStatusTag } from "../../utils/renderStatusTag";
import { useClientPortalAPI } from "@certn/api";
import { BasicButton } from "@certn/cascadia";

interface ProfileCardProps {
  name?: string | null;
  shortID: string;
  status: components["schemas"]["ClientPortalOverallStatus"];
  decision?: components["schemas"]["ClientPortalDecision"];
  score?: components["schemas"]["ClientPortalOverallScore"];
  applicant?: components["schemas"]["ApplicantNameAndID"];
  rescreenFrequency?: string;
  adjudicationRubricDetails?: {
    name: string | null;
    id: string;
  };
}

function ProfileCard({
  name,
  shortID,
  status,
  decision,
  score,
  applicant,
  rescreenFrequency,
  adjudicationRubricDetails,
}: ProfileCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enableClientAdjudication } = useFlags();
  const canViewScore = useCanViewCaseScore();

  return (
    <div className={styles.profileCard}>
      {renderStatusTag({
        status,
        decision,
        score,
        isScoreHidden: !canViewScore,
      })}
      <div className={styles.innerProfileCard}>
        <MediaQuery sm md type="show">
          <Text className={css({ whiteSpace: "nowrap" })}>
            {t("Case ID: ") + shortID}
          </Text>
        </MediaQuery>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "var(--space-4)",
            alignItems: "center",
          }}
        >
          {adjudicationRubricDetails &&
            enableClientAdjudication &&
            status?.name === "COMPLETE" && (
              <div className={styles.popover}>
                <DialogTrigger>
                  <Stack gap="space-0" orientation="horizontal">
                    <GhostIconButton
                      icon="info"
                      color="icon-weak"
                      size="small"
                    />
                    <Text style="global/body-secondary" color="text-weak">
                      {t("Adjudicated")}
                    </Text>
                  </Stack>
                  <Popover placement="top left">
                    <PopoverHeading>{t("Adjudication")}</PopoverHeading>
                    <PopoverContent>
                      <Text style="global/body-secondary" color="text-weak">
                        {t(
                          "Scoring rules have been applied to this case using rubric "
                        )}
                        <Text
                          style="global/body-secondary"
                          className={css({ fontWeight: 600 })}
                        >
                          {adjudicationRubricDetails?.name || "--"}
                        </Text>
                      </Text>
                    </PopoverContent>
                    <PopoverActions>
                      <BasicButton
                        onPress={() =>
                          navigate(`/case-details/${shortID}/adjudication`)
                        }
                      >
                        {t("See details")}
                      </BasicButton>
                    </PopoverActions>
                  </Popover>
                </DialogTrigger>
              </div>
            )}
          {applicant && (
            <Link
              href={`/profiles/${applicant.personal_profile__id}`}
              testID="applicant-profile-link"
            >
              <Text style="global/body-secondary-link">
                {t("View profile")}
              </Text>
            </Link>
          )}
          {rescreenFrequency && rescreenFrequency !== "DO_NOT_RESCREEN" && (
            <Text>
              {t("Rescreen: {{frequency}}", {
                frequency: rescreenFrequency,
              })}
            </Text>
          )}

          <>
            <Text style="global/body-secondary" className={styles.requstor}>
              {t("Requestor")}:
              <AvatarWithName
                gap="space-1"
                name={parseName(name || undefined)}
              />
            </Text>
          </>
        </div>
      </div>
    </div>
  );
}

interface caseDetailsTitleCardProps {
  decision?: components["schemas"]["ClientPortalDecision"];
  groupName?: string | null;
  shortID: string;
  orderedByName?: string | null;
  status: components["schemas"]["ClientPortalOverallStatus"];
  score?: components["schemas"]["ClientPortalOverallScore"];
  updateDecision: (caseShortId: string | undefined, keys: any) => Promise<void>;
  tags: readonly components["schemas"]["CaseDetailsTagSet"][];
  applicant: components["schemas"]["ApplicantNameAndID"];
  checks: readonly components["schemas"]["CaseChecksContent"][];
  rescreenFrequency?: string;
  caseId?: string;
  promptMonitoringEnroll?: boolean;
  monitoringEligibilityRefreshing?: boolean;
  onMonitoringPromptHandled?: () => void;
}

export function CaseDetailsTitleCard({
  decision,
  groupName,
  shortID,
  orderedByName,
  status,
  score,
  updateDecision,
  tags,
  applicant,
  checks,
  rescreenFrequency,
  caseId,
  promptMonitoringEnroll,
  monitoringEligibilityRefreshing,
  onMonitoringPromptHandled,
}: caseDetailsTitleCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [selection, setSelection] = useState<Key[]>([decision?.name || "NONE"]);
  const { clientPortalCaseTags } = useFlags();
  const { details: userDetails } = useUser();
  const pickerAlignment = useMediaQuery(
    `(max-width: ${breakpoints["breakpoint-md"].maxWidth.toString()}px)`
  )
    ? "bottom left"
    : "bottom right";

  const { data } = useClientPortalAPI(
    `/api/internal/client-portal/report/${caseId}/`
  );

  const { data: caseDetails } = useClientPortalAPI(
    `/api/internal/client-portal/case/${caseId}/`
  );

  const isMonitoringCase = caseDetails?.source === "MONITORING";
  const showPicker =
    caseDetails?.can_set_client_decision === true && !isMonitoringCase;

  return (
    <div>
      <div className={styles.container}>
        <div className={styles.infoContainer}>
          <Breadcrumbs
            breadcrumbs={[
              {
                navigate: () => navigate(state?.last?.href || "/cases"),
                title: groupName || "-",
              },
              {
                title: t("Case Details"),
              },
            ]}
          />
          <div className={styles.title}>
            <Text as="h1" style="global/title-medium" testID="heading">
              {data?.report_data.applicant_info?.current_name?.given_name
                ? `${data?.report_data.applicant_info?.current_name.given_name} ${
                    data?.report_data.applicant_info?.current_name
                      .family_name || ""
                  }`
                : data?.report_data.applicant_info?.email}
            </Text>
          </div>
          <ProfileCard
            status={status}
            decision={decision}
            score={score}
            shortID={shortID}
            name={orderedByName}
            applicant={applicant}
            rescreenFrequency={rescreenFrequency}
            adjudicationRubricDetails={
              caseDetails?.adjudication_rubric || undefined
            }
          />
        </div>

        <div className={styles.picker}>
          {applicant?.personal_profile__id && (
            <CaseMonitoringButton
              caseShortId={shortID}
              caseChecks={checks}
              subjectId={applicant.personal_profile__id}
              status={status}
              decision={decision}
              promptEnroll={promptMonitoringEnroll}
              promptEligibilityRefreshing={monitoringEligibilityRefreshing}
              onPromptHandled={onMonitoringPromptHandled}
            />
          )}
          {showPicker && (
            <Picker
              items={[
                { id: "NONE", name: t("None") },
                { id: "MEETS_CRITERIA", name: t("Meets criteria") },
                { id: "CRITERIA_NOT_MET", name: t("Criteria not met") },
              ]}
              label={`${t("Decision")}:`}
              onSelectionChange={async (selection) => {
                const keys = Array.from(selection);
                try {
                  await updateDecision(shortID, keys[0]);
                  setSelection(keys);
                } catch {
                  // Error is already handled and displayed in updateDecision
                  // Just prevent unhandled promise rejection
                }
              }}
              selectedKeys={[selection[0] as AriaKey]}
              selectionMode="single"
              showActionBar={false}
              trigger={(props) => (
                <PrimaryButton
                  isDisabled={userDetails?.is_proxy_user}
                  rightIcon="expand_more"
                  testID="case-details-decision-picker-button"
                >
                  {props.label} {props.selectedItems[0]?.name}
                </PrimaryButton>
              )}
              placement={pickerAlignment}
              testID="case-details-picker"
            />
          )}
          <CaseDetailActionsDropdown checks={checks} />
        </div>
      </div>

      <div className={styles.tags}>
        {clientPortalCaseTags &&
          tags?.map((tag, i: number) => {
            return (
              <NeutralTag type="subtle" key={i}>
                {tag.name}
              </NeutralTag>
            );
          })}
      </div>
    </div>
  );
}

const styles = {
  container: css(
    {
      display: "flex",
      width: "100%",
      justifyContent: "space-between",
      backgroundColor: "var(--surface-base)",
    },
    mq({
      alignItems: ["normal", "normal", "center"],
      flexDirection: ["column", "column", "row"],
    })
  ),
  infoContainer: css(
    {
      display: "flex",
      flexDirection: "column",
      marginBottom: "var(--space-4)",
    },
    mq({
      gap: ["var(--space-2)", "var(--space-2)"],
      paddingLeft: ["var(--space-4)", "var(--space-4)", "var(--space-8)"],
      paddingTop: "var(--space-5)",
    })
  ),
  title: css(
    mq({
      marginTop: [0, 0, "var(--space-2)"],
    })
  ),
  picker: css(
    {
      display: "flex",
      paddingRight: "var(--space-8)",
      gap: "var(--space-4)",
      alignItems: "center",
    },
    mq({
      marginLeft: ["var(--space-4)", "var(--space-4)", 0],
      whiteSpace: ["normal", "nowrap"],
    })
  ),
  profileCard: css(
    {
      display: "flex",
      flexWrap: "wrap",
      gap: "var(--space-2)",
    },
    mq({
      alignItems: ["normal", "normal", "center"],
      flexDirection: ["column", "row"],
    })
  ),
  innerProfileCard: css(
    {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
    },
    mq({
      gap: ["var(--space-2)", "var(--space-4)"],
    })
  ),
  tags: css(
    {
      display: "flex",
      flexDirection: "row",
      gap: "var(--space-1)",
      paddingRight: "var(--space-8)",
      backgroundColor: "var(--surface-base)",
      paddingTop: "var(--space-5)",
      paddingBottom: "var(--space-5)",
    },
    mq({
      justifyContent: ["flex-start", "flex-start", "flex-end"],
      paddingLeft: ["var(--space-4)", "var(--space-4)", 0],
      paddingTop: ["var(--space-3)", "var(--space-3)", 0],
      paddingBottom: ["var(--space-5)", "var(--space-5)", 0],
    })
  ),
  requstor: css({
    display: "flex",
    flexDirection: "row",
    gap: "var(--space-1)",
    alignItems: "baseline",
  }),
  popover: css({
    display: "flex",
    gap: "var(--space-1)",
    alignItems: "center",
  }),
  popoverContent: css({
    display: "flex",
    gap: "var(--space-2)",
    flexDirection: "column",
    paddingTop: "var(--space-2)",
    paddingLeft: "var(--space-4)",
    paddingBottom: "var(--space-2)",
    paddingRight: "var(--space-4)",
  }),
};
